import { toast as sonnerToast } from "@prelude-ai/design-system"
import { CircleCheck, Info, OctagonX, TriangleAlert, X } from "lucide-react"

type ToastVariant = "error" | "success" | "info" | "warning"

type ToastOptions = {
  title: string
  description?: string
  variant?: ToastVariant
}

const variantStyles: Record<ToastVariant, { border: string; icon: string; bg: string }> = {
  error: {
    border: "border-destructive/40",
    icon: "text-destructive",
    bg: "bg-destructive/10"
  },
  success: {
    border: "border-green-500/40",
    icon: "text-green-500",
    bg: "bg-green-500/10"
  },
  info: {
    border: "border-blue-500/40",
    icon: "text-blue-500",
    bg: "bg-blue-500/10"
  },
  warning: {
    border: "border-yellow-500/40",
    icon: "text-yellow-500",
    bg: "bg-yellow-500/10"
  }
}

const icons: Record<ToastVariant, React.ReactNode> = {
  error: <OctagonX className="size-4 shrink-0" />,
  success: <CircleCheck className="size-4 shrink-0" />,
  info: <Info className="size-4 shrink-0" />,
  warning: <TriangleAlert className="size-4 shrink-0" />
}

export function toast({ title, description, variant = "error" }: ToastOptions) {
  const styles = variantStyles[variant]

  return sonnerToast.custom((id) => (
    <div className={`flex w-full items-center gap-3 rounded-lg border ${styles.border} bg-background p-4 shadow-lg`}>
      <div className={`flex size-6 items-center justify-center rounded-full ${styles.bg}`}>
        <span className={styles.icon}>{icons[variant]}</span>
      </div>
      <div className="flex-1 justify-self-center min-w-0">
        {title && <p className="text-sm font-medium text-foreground">{title}</p>}
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      <button
        type="button"
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => sonnerToast.dismiss(id)}
      >
        <X className="size-4" />
      </button>
    </div>
  ))
}

toast.error = (title: string, options?: { description?: string }) =>
  toast({ title, description: options?.description, variant: "error" })

toast.success = (title: string, options?: { description?: string }) =>
  toast({ title, description: options?.description, variant: "success" })

toast.info = (title: string, options?: { description?: string }) =>
  toast({ title, description: options?.description, variant: "info" })

toast.warning = (title: string, options?: { description?: string }) =>
  toast({ title, description: options?.description, variant: "warning" })
